import {
  ADS_RELEASE_DATE_UNIX_TS,
  DEFAULT_NON_AWL_SUPPORT_CENTER_URL,
  LOCATIONS_RELEASE_DATE_UNIX_TS,
  modalName,
  nonTranslatableText,
  pathName,
  tabNames,
} from '@va/constants';
//This import is required in order to work all locale languages.
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import { getConversionTypes, WEBSITE_REQUEST, WEBSITE_SUCCEEDED } from '@va/dashboard/actions/api';
import {
  addNotification,
  SET_ACTIVE_TAB,
  SET_GLOBAL_FILTER,
  setCustomizationOptions,
  setGlobalFilter,
} from '@va/dashboard/actions/ui';
import { getFeatureReleaseDate, getInstanceId } from '@va/dashboard/selectors/app';
import { getWebsite, hasWebsite } from '@va/dashboard/selectors/core';
import { getGlobalFilter, isModalOpen } from '@va/dashboard/selectors/ui';
import { appHistory } from '@va/shared/router';
import { getGoogleImporterProgress, LOGIN_SUCCEEDED } from '@va/standalone/shared/actions';
import { modalName as standaloneModals } from '@va/standalone/shared/constants';
import {
  getConfigChatwootToken,
  isAccountSettingsPageSelected,
  isUserLoggedInWithCustomUrl,
} from '@va/standalone/shared/helpers';
import { getWebsites, hasFreePlan as hasFreePlanSelector, selectAgencyUi } from '@va/standalone/shared/selectors';
import {
  isStandaloneApp,
  isThemeApplied,
  setCustomTheme,
  setDefaultTheme,
  storeTimePeriodToLocalStorage,
} from '@va/util/helpers';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/pt';
import 'moment/locale/ro';
import { all, put, select, spawn, take, takeLatest } from 'redux-saga/effects';
import { updateGlobalFilterUrlParam } from './uiTs';

export function* watchers() {
  yield all([
    takeLatest(SET_ACTIVE_TAB, handleSetActiveTab),
    takeLatest(SET_GLOBAL_FILTER, persistGlobalFilterToSessionStorage),
    // VISA-10395 - After logout the customization state is reset, so we need to set it back after login
    takeLatest(LOGIN_SUCCEEDED, handleSetCustomizationOptions),
    takeLatest(SET_GLOBAL_FILTER, updateGlobalFilterUrlParam),
  ]);
}

function* websiteRequestSucceeded() {
  const websiteRequest = yield take([WEBSITE_REQUEST, WEBSITE_SUCCEEDED]);

  return websiteRequest.type === WEBSITE_SUCCEEDED;
}

function* resetFilterDownToFeatureReleaseDate(releaseDate) {
  const website = yield select(getWebsite);
  const installDate = website.installDate;
  let filter = yield select(getGlobalFilter);
  const fromDate = filter.from.unix();
  if (releaseDate && releaseDate > installDate && fromDate < releaseDate) {
    filter = {
      from: moment(releaseDate * 1000).startOf('day'),
      until: moment().endOf('day'),
    };
    yield put(setGlobalFilter(filter));
  }
}

export function* persistGlobalFilterToSessionStorage(action) {
  const websiteId = yield select(getInstanceId);
  const website = yield select(getWebsite);
  const hasFreePlan = yield select(hasFreePlanSelector);

  storeTimePeriodToLocalStorage({
    from: action.filter?.from,
    until: action.filter?.until,
    websiteId: websiteId,
    installDate: website.installDate,
    hasFreePlan,
    dateRangeKey: action.filter?.dateRangeKey,
  });
}

export function* handleSetActiveTab(action) {
  if (isAccountSettingsPageSelected() && !isUserLoggedInWithCustomUrl()) {
    setDefaultTheme();
  }

  if (!isAccountSettingsPageSelected()) {
    const website = yield select(getWebsite);
    if (!website.whiteLabel) return;

    const agencyUi = yield select(selectAgencyUi);
    if (!agencyUi) return;

    const { theme, fontName, logoId } = agencyUi;
    if (!isThemeApplied({ fontName, colors: theme?.colors })) {
      setCustomTheme({ theme, fontName, logoId });
    }
  }

  const websiteExists = yield select(hasWebsite);

  const allWebsites = yield select(getWebsites);
  const isAddWebsiteModalOpen = yield select(isModalOpen, modalName.addWebsite);
  const isContributorConfirmationModalOpen = yield select(isModalOpen, standaloneModals.CONFIRM_CONTRIBUTOR_REQUEST);

  //Check if there is no websites in the account, do now allow user to visit any other tab until he/she creates one.
  if (
    allWebsites?.get('total') === 0 &&
    isStandaloneApp() &&
    !isContributorConfirmationModalOpen &&
    ![tabNames.accountSettings, tabNames.addWebsite, tabNames.consent].includes(action.tab)
  ) {
    appHistory.push(pathName.manageWebsites);
    if (!isAddWebsiteModalOpen && action.tab !== 'manageWebsites') {
      yield put(
        addNotification({
          title: 'notifications.notifyToAddWebsite',
          level: 'error',
          autoDismiss: 10,
        }),
      );
    }
    return;
  }

  if (websiteExists || spawn(websiteRequestSucceeded)) {
    const filter = yield select(getGlobalFilter);

    switch (action.tab) {
      case tabNames.latestVisitorsLocations: {
        const featureReleaseDate = yield select(getFeatureReleaseDate, LOCATIONS_RELEASE_DATE_UNIX_TS);
        yield spawn(resetFilterDownToFeatureReleaseDate, featureReleaseDate);
        break;
      }
      case tabNames.adsOverview: {
        const featureReleaseDate = yield select(getFeatureReleaseDate, ADS_RELEASE_DATE_UNIX_TS);
        yield spawn(resetFilterDownToFeatureReleaseDate, featureReleaseDate);
        break;
      }
      case tabNames.myCampaigns: {
        const featureReleaseDate = yield select(getFeatureReleaseDate, ADS_RELEASE_DATE_UNIX_TS);
        yield spawn(resetFilterDownToFeatureReleaseDate, featureReleaseDate);
        break;
      }

      case tabNames.pageSettings:
        yield put(getConversionTypes());

        break;
      case tabNames.import:
        yield put(getGoogleImporterProgress());
        break;
      default:
        return null;
    }
  }
}

function* handleSetCustomizationOptions() {
  yield put(
    setCustomizationOptions({
      providerName: nonTranslatableText.appName,
      supportCenterDomain: DEFAULT_NON_AWL_SUPPORT_CENTER_URL,
      chatwootToken: getConfigChatwootToken(),
    }),
  );
}
